import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  Users,
  UserPlus,
  Receipt,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Building2,
  MapPin,
  Wallet,
  Activity,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { useDashboard } from "../../hooks/useQueries";
import { getErrorMessage } from "../../lib/utils/errorMessage";

interface DashboardData {
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
  summary: {
    donations: {
      count: number;
      amount: number;
      averageAmount: number;
      change: { count: number; amount: number };
    };
    expenses: {
      count: number;
      amount: number;
      averageAmount: number;
      change: { count: number; amount: number };
    };
    netCashFlow: {
      amount: number;
      change: number;
    };
    donors: {
      permanent: number;
      guest: number;
      total: number;
    };
    offices: number;
    regions: number;
    accounts: number;
  };
  breakdowns: {
    donationsByType: Array<{
      typeId: string;
      typeName: string;
      count: number;
      amount: number;
      percentage: string;
    }>;
    expensesByType: Array<{
      typeId: string;
      typeName: string;
      count: number;
      amount: number;
      percentage: string;
    }>;
  };
  trends: {
    daily: Array<{ date: string; count: number; amount: number }>;
    weekly: Array<{
      year: number;
      week: number;
      count: number;
      amount: number;
    }>;
    monthly: Array<{
      year: number;
      month: number;
      count: number;
      amount: number;
    }>;
  };
  topPerformers: {
    donors: Array<{
      donorId: string;
      donorName: string;
      donorType: string;
      count: number;
      amount: number;
    }>;
    offices: Array<{
      officeId: string;
      officeName: string;
      count: number;
      amount: number;
      donorCount: number;
    }>;
    regions: Array<{
      regionId: string;
      regionName: string;
      count: number;
      amount: number;
    }>;
  };
  recentActivity: {
    donations: Array<{
      receiptId: string;
      donorName: string;
      amount: number;
      type: string;
      office: string;
      date: string;
    }>;
    expenses: Array<{
      expenseId: string;
      description: string;
      amount: number;
      type: string;
      office: string;
      date: string;
    }>;
  };
  accounts: Array<{
    accountId: string;
    name: string;
    balance: number;
    type: string;
  }>;
  generatedAt: string;
  userRole: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [period, setPeriod] = useState("30");

  // Use React Query hook with caching
  const {
    data: dashboardData,
    isLoading: loading,
    error,
    refetch,
  } = useDashboard(period);

  const formatCurrency = (amount: number) => {
    return `Rs. ${amount.toLocaleString("en-PK", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-PK", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-PK", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <p className="text-red-600 dark:text-red-400">
            {getErrorMessage(error, 'Failed to load dashboard data.')}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  const { summary, breakdowns, topPerformers, recentActivity, accounts } =
    dashboardData;

  const stats = [
    {
      name: "Total Donations",
      value: formatCurrency(summary.donations.amount),
      change: summary.donations.change.amount,
      changeType:
        summary.donations.change.amount >= 0
          ? ("increase" as const)
          : ("decrease" as const),
      icon: CreditCard,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
      subtitle: `${summary.donations.count} donations`,
    },
    {
      name: "Total Expenses",
      value: formatCurrency(summary.expenses.amount),
      change: summary.expenses.change.amount,
      changeType:
        summary.expenses.change.amount >= 0
          ? ("increase" as const)
          : ("decrease" as const),
      icon: Receipt,
      color: "text-red-600 dark:text-red-400",
      bgColor: "bg-red-100 dark:bg-red-900/20",
      subtitle: `${summary.expenses.count} expenses`,
    },
    {
      name: "Net Cash Flow",
      value: formatCurrency(summary.netCashFlow.amount),
      change: summary.netCashFlow.change,
      changeType:
        summary.netCashFlow.amount >= 0
          ? ("increase" as const)
          : ("decrease" as const),
      icon: DollarSign,
      color:
        summary.netCashFlow.amount >= 0
          ? "text-blue-600 dark:text-blue-400"
          : "text-orange-600 dark:text-orange-400",
      bgColor:
        summary.netCashFlow.amount >= 0
          ? "bg-blue-100 dark:bg-blue-900/20"
          : "bg-orange-100 dark:bg-orange-900/20",
      subtitle: `Avg: ${formatCurrency(summary.donations.averageAmount)} per donation`,
    },
    {
      name: "Total Donors",
      value: (
        summary.donors.total ||
        (summary.donors.permanent || 0) + (summary.donors.guest || 0)
      ).toLocaleString(),
      change: 0,
      changeType: "increase" as const,
      icon: Users,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
      subtitle: `${summary.donors.permanent || 0} permanent, ${summary.donors.guest || 0} guest`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Welcome back, {user?.name || "User"}! Here's what's happening with
            your organization.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="1">Today</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button
            onClick={() => refetch()}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.name}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stat.value}
                  </p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {stat.subtitle}
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
              {stat.change !== 0 && (
                <div className="mt-4 flex items-center">
                  {stat.changeType === "increase" ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <span
                    className={`ml-2 text-sm font-medium ${
                      stat.changeType === "increase"
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {stat.change > 0 ? "+" : ""}
                    {stat.change.toFixed(1)}%
                  </span>
                  <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                    vs previous period
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Additional Stats for SuperAdmin */}
      {user?.role === "SuperAdmin" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Offices
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {summary.offices}
                  </p>
                </div>
                <Building2 className="h-8 w-8 text-blue-500" />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Regions
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {summary.regions}
                  </p>
                </div>
                <MapPin className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Active Accounts
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {summary.accounts}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Donations by Type */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Donations by Type
                  </h3>
                </div>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {breakdowns.donationsByType.length > 0 ? (
                    breakdowns.donationsByType.map((item) => (
                      <div
                        key={item.typeId}
                        className="flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.typeName}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {item.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {item.count} donations
                            </span>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No donations in this period
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Expenses by Type */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-red-600" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Expenses by Type
                  </h3>
                </div>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {breakdowns.expensesByType.length > 0 ? (
                    breakdowns.expensesByType.map((item) => (
                      <div
                        key={item.typeId}
                        className="flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.typeName}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {item.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-red-600 h-2 rounded-full"
                              style={{ width: `${item.percentage}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {item.count} expenses
                            </span>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No expenses in this period
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Donors */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Top Donors
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {topPerformers.donors.length > 0 ? (
                    topPerformers.donors.slice(0, 5).map((donor, index) => (
                      <div
                        key={donor.donorId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
                              #{index + 1}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {donor.donorName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {donor.count} donations
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900 dark:text-white">
                            {formatCurrency(donor.amount)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No donors in this period
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Top Offices */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Top Offices
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {topPerformers.offices.length > 0 ? (
                    topPerformers.offices.slice(0, 5).map((office, index) => (
                      <div
                        key={office.officeId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                              #{index + 1}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {office.officeName}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {office.donorCount} donors, {office.count}{" "}
                              donations
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900 dark:text-white">
                            {formatCurrency(office.amount)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No offices in this period
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Recent Donations
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-3 max-h-[380px] overflow-y-auto pr-2 custom-scrollbar">
                {recentActivity.donations.length > 0 ? (
                  recentActivity.donations.slice(0, 15).map((donation) => (
                    <div
                      key={donation.receiptId}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {donation.donorName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {donation.type} • {donation.office}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {formatCurrency(donation.amount)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(donation.date)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No recent donations
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Account Balances */}
          {accounts.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Account Balances
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <div
                      key={account.accountId}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {account.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {account.type}
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-sm font-bold ${account.balance >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                        >
                          {formatCurrency(account.balance)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
